/** API Keys tab — create, list, edit, revoke API keys (scopes plus opt-in agent secret flags) */

import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Key, Plus, Copy, Check, AlertTriangle, Pencil } from 'lucide-react'
import { AGENT_SECRET_FLAGS } from '@agentwiki/shared'
import { apiClient } from '../../lib/api-client'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { ScopePicker } from './scope-picker'
import { SettingsSectionHeader } from './settings-section-header'
import {
  badgeClass,
  buttonDangerClass,
  buttonPrimaryClass,
  buttonSecondaryClass,
  cardClass,
  errorTextClass,
  iconButtonClass,
  inputClass,
  labelClass,
} from '../ui/styles'

interface ApiKey {
  id: string
  name: string
  keyPrefix: string
  scopes: string[]
  lastUsedAt: string | null
  expiresAt: string | null
  createdAt: string
}

interface CreatedKey {
  id: string
  key: string
  keyPrefix: string
  name: string
  scopes: string[]
}

type KeyUpdate = { name?: string; scopes?: string[]; expiresInDays?: number | null }

const AGENT_FLAG_SET = new Set<string>(AGENT_SECRET_FLAGS)

const DEFAULT_SCOPES = ['doc:read']

/** Positive whole number of days from a text input, else undefined */
function parseDays(value: string): number | undefined {
  const days = parseInt(value, 10)
  return !isNaN(days) && days > 0 ? days : undefined
}

export function ApiKeysTab() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [newKey, setNewKey] = useState<CreatedKey | null>(null)
  const [copied, setCopied] = useState(false)

  const [keyName, setKeyName] = useState('')
  const [scopes, setScopes] = useState<string[]>(DEFAULT_SCOPES)
  const [expiresInDays, setExpiresInDays] = useState('')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiClient.get<{ keys: ApiKey[] }>('/api/keys'),
  })

  const createKey = useMutation({
    mutationFn: (body: { name: string; scopes: string[]; expiresInDays?: number }) => apiClient.post<CreatedKey>('/api/keys', body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['api-keys'] })
      setNewKey(result)
      setShowForm(false)
      setKeyName('')
      setScopes(DEFAULT_SCOPES)
      setExpiresInDays('')
    },
  })

  const updateKey = useMutation({
    mutationFn: ({ id, ...body }: KeyUpdate & { id: string }) => apiClient.patch<ApiKey>(`/api/keys/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })

  const revokeKey = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  })

  const copyKey = async () => {
    if (!newKey) return
    await navigator.clipboard.writeText(newKey.key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCreate = (e: FormEvent) => {
    e.preventDefault()
    if (!keyName.trim() || scopes.length === 0) return
    const days = parseDays(expiresInDays)
    createKey.mutate({ name: keyName.trim(), scopes, ...(days ? { expiresInDays: days } : {}) })
  }

  const keys = data?.keys ?? []
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })

  return (
    <div className="space-y-4">
      <SettingsSectionHeader
        title={t('settings.apiKeys.title')}
        description={t('settings.apiKeys.subtitle')}
        action={
          <button
            type="button"
            onClick={() => {
              setShowForm((v) => !v)
              setNewKey(null)
            }}
            className={buttonPrimaryClass}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('settings.apiKeys.create')}
          </button>
        }
      />

      {showForm && (
        <form onSubmit={handleCreate} className={cn(cardClass, 'space-y-4 p-4')}>
          <h3 className="text-sm font-semibold text-fg">{t('settings.apiKeys.newKey')}</h3>
          <div className="space-y-1.5">
            <label htmlFor="api-key-name" className={labelClass}>
              {t('settings.apiKeys.name')}
            </label>
            <input
              id="api-key-name"
              type="text"
              placeholder={t('settings.apiKeys.namePlaceholder')}
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              maxLength={100}
              className={inputClass}
            />
          </div>
          <div>
            <span className={labelClass}>{t('settings.apiKeys.scopes')}</span>
            <ScopePicker scopes={scopes} onChange={setScopes} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="api-key-expiry" className={labelClass}>
              {t('settings.apiKeys.expiresIn')}
            </label>
            <input
              id="api-key-expiry"
              type="number"
              placeholder="90"
              min="1"
              max="365"
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              className={cn(inputClass, 'w-32')}
            />
          </div>
          {createKey.isError && <p role="alert" className={errorTextClass}>{t('settings.apiKeys.createFailed', { message: createKey.error.message })}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={createKey.isPending || !keyName.trim() || scopes.length === 0} className={buttonPrimaryClass}>
              {createKey.isPending ? t('settings.apiKeys.creating') : t('settings.apiKeys.createSubmit')}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className={buttonSecondaryClass}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {newKey && (
        <div role="status" className="space-y-2 rounded-lg border border-warning/30 bg-warning-soft p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('settings.apiKeys.saveNow')}
          </p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 select-all break-all rounded-md bg-surface px-3 py-2 font-mono text-sm text-fg">{newKey.key}</code>
            <button type="button" onClick={() => void copyKey()} aria-label={t('settings.apiKeys.copy')} title={t('settings.apiKeys.copy')} className={iconButtonClass}>
              {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <button type="button" onClick={() => setNewKey(null)} className="cursor-pointer text-xs text-fg-subtle hover:text-fg">
            {t('settings.dismiss')}
          </button>
        </div>
      )}

      {isLoading ? (
        <p className="py-6 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <p role="alert" className={errorTextClass}>{t('settings.loadError')}</p>
      ) : keys.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-fg-subtle">
          <Key className="h-6 w-6 opacity-50" aria-hidden="true" />
          <p className="text-sm">{t('settings.apiKeys.empty')}</p>
        </div>
      ) : (
        <ul className={cn(cardClass, 'divide-y divide-border')}>
          {keys.map((k) => (
            <KeyRow
              key={k.id}
              apiKey={k}
              dateFormat={dateFormat}
              onUpdate={(updates) => updateKey.mutateAsync({ id: k.id, ...updates })}
              onRevoke={() => {
                if (window.confirm(t('settings.apiKeys.revokeConfirm', { name: k.name }))) revokeKey.mutate(k.id)
              }}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

/** Single API key row with inline edit */
function KeyRow({
  apiKey: k,
  dateFormat,
  onUpdate,
  onRevoke,
}: {
  apiKey: ApiKey
  dateFormat: Intl.DateTimeFormat
  onUpdate: (updates: KeyUpdate) => Promise<unknown>
  onRevoke: () => void
}) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [editName, setEditName] = useState(k.name)
  const [editScopes, setEditScopes] = useState<string[]>(k.scopes)
  const [editExpiry, setEditExpiry] = useState('')
  const [clearExpiry, setClearExpiry] = useState(false)

  const startEdit = () => {
    setEditName(k.name)
    setEditScopes([...k.scopes])
    setEditExpiry('')
    setClearExpiry(false)
    setError('')
    setEditing(true)
  }

  const handleSave = async (e: FormEvent) => {
    e.preventDefault()
    const updates: KeyUpdate = {}
    if (editName.trim() !== k.name) updates.name = editName.trim()
    if (JSON.stringify([...editScopes].sort()) !== JSON.stringify([...k.scopes].sort())) updates.scopes = editScopes
    if (clearExpiry && k.expiresAt) {
      updates.expiresInDays = null
    } else {
      const days = parseDays(editExpiry)
      if (days) updates.expiresInDays = days
    }
    if (Object.keys(updates).length === 0) {
      setEditing(false)
      return
    }
    setSaving(true)
    setError('')
    try {
      await onUpdate(updates)
      setEditing(false)
    } catch (err) {
      setError(t('settings.apiKeys.updateFailed', { message: err instanceof Error ? err.message : String(err) }))
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <li>
        <form onSubmit={handleSave} className="space-y-3 p-4">
          <h3 className="text-sm font-semibold text-fg">{t('settings.apiKeys.editKey')}</h3>
          <div className="space-y-1.5">
            <label htmlFor={`key-name-${k.id}`} className={labelClass}>
              {t('settings.apiKeys.name')}
            </label>
            <input id={`key-name-${k.id}`} type="text" value={editName} onChange={(e) => setEditName(e.target.value)} maxLength={100} className={inputClass} />
          </div>
          <div>
            <span className={labelClass}>{t('settings.apiKeys.scopes')}</span>
            <ScopePicker scopes={editScopes} onChange={setEditScopes} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor={`key-expiry-${k.id}`} className={labelClass}>
              {t('settings.apiKeys.newExpiry')}
            </label>
            <input
              id={`key-expiry-${k.id}`}
              type="number"
              min="1"
              max="365"
              value={editExpiry}
              onChange={(e) => {
                setEditExpiry(e.target.value)
                setClearExpiry(false)
              }}
              disabled={clearExpiry}
              placeholder="90"
              className={cn(inputClass, 'w-32')}
            />
            {k.expiresAt && (
              <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm text-fg-muted">
                <input
                  type="checkbox"
                  checked={clearExpiry}
                  onChange={(e) => {
                    setClearExpiry(e.target.checked)
                    if (e.target.checked) setEditExpiry('')
                  }}
                  className="h-4 w-4 rounded accent-accent"
                />
                {t('settings.apiKeys.removeExpiry')}
              </label>
            )}
          </div>
          {error && <p role="alert" className={errorTextClass}>{error}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={saving || !editName.trim() || editScopes.length === 0} className={buttonPrimaryClass}>
              {saving ? t('settings.saving') : t('settings.save')}
            </button>
            <button type="button" onClick={() => setEditing(false)} className={buttonSecondaryClass}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
      <Key className="mt-0.5 hidden h-4 w-4 shrink-0 text-fg-subtle sm:block" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="break-all text-sm font-medium text-fg">{k.name}</span>
          <code className={cn(badgeClass, 'font-mono')}>{k.keyPrefix}…</code>
        </div>
        <div className="flex flex-wrap gap-1">
          {k.scopes.map((s) => (
            <span
              key={s}
              className={cn(
                'rounded-md px-1.5 py-0.5 font-mono text-xs',
                AGENT_FLAG_SET.has(s) ? 'bg-warning-soft text-warning' : 'bg-accent-soft text-accent-text',
              )}
            >
              {s}
            </span>
          ))}
        </div>
        <p className="flex flex-wrap gap-x-3 text-xs text-fg-subtle">
          <span>{t('settings.apiKeys.created', { date: dateFormat.format(new Date(k.createdAt)) })}</span>
          {k.lastUsedAt && <span>{t('settings.apiKeys.lastUsed', { date: dateFormat.format(new Date(k.lastUsedAt)) })}</span>}
          {k.expiresAt && <span>{t('settings.apiKeys.expires', { date: dateFormat.format(new Date(k.expiresAt)) })}</span>}
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <button type="button" onClick={startEdit} aria-label={t('settings.apiKeys.editNamed', { name: k.name })} className={buttonSecondaryClass}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          {t('settings.edit')}
        </button>
        <button type="button" onClick={onRevoke} className={buttonDangerClass}>
          {t('settings.apiKeys.revoke')}
        </button>
      </div>
    </li>
  )
}
